import { useState } from "react";
import { Label } from "@groot/ui/label";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { NumberInput } from "@/components/number-input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { toast } from "sonner";
import { useCreateOrder } from "@/pages/order/hooks/useOrders";
import { useCustomers } from "@/pages/customer/hooks/useCustomers";
import { useActiveProducts } from "@/pages/product/hooks/useProducts";
import { Plus, Minus } from "lucide-react";

const orderItemSchema = z.object({
  productName: z.string().min(1, "Product name is required"),
  size: z.string().min(1, "Size is required"),
  quantity: z.coerce.number().int().positive("Quantity must be positive"),
  unitPrice: z.coerce.number().positive("Unit price must be positive"),
  printFormat: z.enum(["mini", "medium", "large"]).nullable().optional(),
});

const orderSchema = z.object({
  customerId: z.coerce.number().int().positive("Customer is required"),
  notes: z.string().optional(),
  discountType: z.enum(["flat", "percentage"]).optional(),
  discountValue: z.coerce.number().nonnegative().optional(),
  discountReason: z.string().optional(),
  items: z.array(orderItemSchema).min(1, "At least one item is required"),
});

type OrderFormValues = z.infer<typeof orderSchema>;

interface CreateOrderDialogProps {
  trigger: React.ReactNode;
  preselectedCustomerId?: number;
}

export function CreateOrderDialog({ trigger, preselectedCustomerId }: CreateOrderDialogProps) {
  const createOrder = useCreateOrder();
  const { data: customers } = useCustomers();
  const { data: products } = useActiveProducts();
  const [open, setOpen] = useState(false);
  const [orderItems, setOrderItems] = useState<
    Array<{
      productName: string;
      size: string;
      quantity: number;
      unitPrice: number;
      printFormat?: "mini" | "medium" | "large" | null;
    }>
  >([]);

  const [discountType, setDiscountType] = useState<"flat" | "percentage" | "">("");
  const [discountValue, setDiscountValue] = useState<string>("");
  const [discountReason, setDiscountReason] = useState("");
  const [shippingCost, setShippingCost] = useState<string>("60");

  const form = useForm<z.input<typeof orderSchema>, unknown, OrderFormValues>({
    resolver: zodResolver(orderSchema),
    defaultValues: {
      customerId: preselectedCustomerId ?? 0,
      notes: "",
      items: [],
    },
  });

  const addItem = () => {
    const updated = [...orderItems, { productName: "", size: "", quantity: 1, unitPrice: 0 }];
    setOrderItems(updated);
    form.setValue("items", updated, { shouldValidate: true });
  };

  const removeItem = (index: number) => {
    const updated = orderItems.filter((_, i) => i !== index);
    setOrderItems(updated);
    form.setValue("items", updated, { shouldValidate: true });
  };

  const updateItem = (index: number, field: string, value: string | number) => {
    setOrderItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      form.setValue("items", updated, { shouldValidate: true });
      return updated;
    });
  };

  const updateItemFields = (index: number, fields: Record<string, string | number | null>) => {
    setOrderItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...fields };
      form.setValue("items", updated, { shouldValidate: true });
      return updated;
    });
  };

  const onSubmit = async (values: OrderFormValues) => {
    try {
      await createOrder.mutateAsync({
        ...values,
        discountType: discountType || undefined,
        discountValue: discountValue !== "" ? parseFloat(discountValue) : undefined,
        discountReason: discountReason || undefined,
        shippingCost: shippingCost !== "" ? parseFloat(shippingCost) : undefined,
      });
      toast.success("Success", { description: "Order created" });
      form.reset();
      setOrderItems([]);
      setDiscountType("");
      setDiscountValue("");
      setDiscountReason("");
      setShippingCost("60");
      setOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to create order" });
    }
  };

  const subtotal = orderItems.reduce(
    (sum, item) =>
      sum +
      (Number.isNaN(item.unitPrice) ? 0 : item.unitPrice) *
        (Number.isNaN(item.quantity) ? 0 : item.quantity),
    0,
  );
  const discountNum = discountValue !== "" ? parseFloat(discountValue) || 0 : 0;
  const discountAmount =
    discountType === "percentage" ? Math.round((subtotal * discountNum) / 100) : discountNum;
  const shippingCostNum = shippingCost !== "" ? parseFloat(shippingCost) || 0 : 0;
  const total = subtotal - discountAmount + shippingCostNum;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>Create Order</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {!preselectedCustomerId && (
            <div className="space-y-2">
              <Label>Customer</Label>
              <Select onValueChange={(value) => form.setValue("customerId", parseInt(value))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers?.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id.toString()}>
                      {customer.name}
                      {customer.mobileNumber ? ` (${customer.mobileNumber})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.customerId && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.customerId.message}
                </p>
              )}
            </div>
          )}

          {/* Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Items</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addItem}>
                <Plus className="size-4" />
                Add Item
              </Button>
            </div>

            {orderItems.length === 0 && (
              <p className="text-sm text-muted-foreground py-2">No items added yet.</p>
            )}

            <div className="space-y-3">
              {orderItems.map((item, index) => (
                <div key={index} className="border rounded-lg p-3 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 space-y-1">
                      <Select
                        value={item.productName}
                        onValueChange={(value) => {
                          const product = products?.find((p) => p.name === value);
                          if (product) {
                            updateItemFields(index, {
                              productName: product.name,
                              size: product.size,
                              unitPrice: Number(product.unitPrice),
                              printFormat: product.printFormat ?? null,
                            });
                          }
                        }}
                      >
                        <SelectTrigger className="h-9 text-sm font-medium border border-dashed border-border bg-muted/50 hover:bg-muted/50 hover:border-foreground/30">
                          <SelectValue placeholder="Select product..." />
                        </SelectTrigger>
                        <SelectContent>
                          {products?.map((product) => (
                            <SelectItem key={product.id} value={product.name}>
                              {product.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {item.size && (
                        <p className="text-xs text-muted-foreground pl-1">{item.size}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="size-9 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
                      onClick={() => removeItem(index)}
                    >
                      <Minus className="size-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-normal text-muted-foreground">
                        Unit Price (₹)
                      </Label>
                      <NumberInput
                        value={item.unitPrice}
                        onValueChange={(v) => updateItem(index, "unitPrice", v)}
                        className="h-9 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-normal text-muted-foreground">Quantity</Label>
                      <NumberInput
                        value={item.quantity}
                        onValueChange={(v) => updateItem(index, "quantity", v)}
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                  <div className="text-right text-sm font-semibold text-foreground">
                    ₹
                    {(
                      (Number.isNaN(item.unitPrice) ? 0 : item.unitPrice) *
                      (Number.isNaN(item.quantity) ? 0 : item.quantity)
                    ).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>

            {form.formState.errors.items && (
              <p className="text-sm text-destructive">{form.formState.errors.items.message}</p>
            )}
          </div>

          {/* Shipping Cost */}
          <div className="space-y-2">
            <Label>Shipping Cost (₹)</Label>
            <NumberInput
              value={parseFloat(shippingCost) || 0}
              onValueChange={(v) => setShippingCost(String(v))}
              className="h-9 text-sm"
            />
          </div>

          {/* Discount */}
          <div className="space-y-2">
            <Label>Discount</Label>
            <div className="flex gap-2">
              <Select
                value={discountType}
                onValueChange={(value) => setDiscountType(value as "flat" | "percentage" | "")}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat">Flat ₹</SelectItem>
                  <SelectItem value="percentage">%</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                placeholder="Value"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                className="flex-1"
              />
            </div>
            <Input
              placeholder="Reason (optional)"
              value={discountReason}
              onChange={(e) => setDiscountReason(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Input placeholder="Order notes (optional)" {...form.register("notes")} />
          </div>
        </div>

        {/* Footer with totals + submit */}
        <div className="border-t px-6 py-4 space-y-3 bg-muted/50">
          {orderItems.length > 0 && (
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Subtotal</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
          )}
          {discountAmount > 0 && (
            <div className="flex justify-between text-sm text-destructive">
              <span>Discount{discountType === "percentage" && ` (${discountNum}%)`}</span>
              <span>-₹{discountAmount}</span>
            </div>
          )}
          {orderItems.length > 0 && (
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Shipping</span>
              <span>₹{shippingCostNum.toFixed(2)}</span>
            </div>
          )}
          {orderItems.length > 0 && (
            <div className="flex justify-between text-sm font-semibold text-foreground">
              <span>Total</span>
              <span>₹{total.toFixed(2)}</span>
            </div>
          )}
          <Button
            className="w-full"
            type="button"
            disabled={createOrder.isPending}
            onClick={form.handleSubmit(onSubmit)}
          >
            {createOrder.isPending ? "Creating..." : "Create Order"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
